<template>
  <nav>
    <!-- Main menu items loop -->
    <div 
      v-for="item in mainMenuItems"
      :key="item.id"
      class="menu-item-container"
    >
      <!-- Main menu link -->
      <NuxtLink 
        :to="item.attributes.link"
        :title="item.attributes.label"
        active-class="nav-active"
        class="nav-link"
      >{{ item.attributes.label }}</NuxtLink>
    </div>

    <div class="menu-item-container">
      <NuxtLink active-class="nav-active" to="/cart" alt="Cart" :class="['main-nav-icon', 'nav-link', { 'wiggle-effect': shouldWiggle }]">
        <i class="fa-solid fa-cart-shopping">
          <span :class="['cart-badge', 'cart-badge-warning', {'cart-empty': !numProducts}]" id='cart-count' v-html="numProducts"></span>
        </i>
      </NuxtLink>

      <ul class="sub-menu">
        <li>
          <CartSubMenu />
        </li>
      </ul>
    </div>  
    <div class="menu-item-container">
      <NuxtLink active-class="nav-active" to="/account" alt="Account" @click="checkLogin" class="main-nav-icon nav-link">
        <i class="fa-solid fa-user"></i>
      </NuxtLink>
      <!-- Sub-menu -->
      <ul class="sub-menu">
        <li v-if="isLoggedIn" class="user-email">{{ userEmail }}<br><a href="#" @click.prevent="handleLogout" class="logout-link">Logout</a></li>
        <li v-else><LoyaltyDisclaimer :onlyText="true" /></li>
      </ul>
      
    </div>
  </nav>
</template>


<script setup lang="ts">
import { ref, onMounted, computed, watch, onBeforeUnmount } from 'vue';
import { useUserStore } from '~/store/userStore';
import { useCartStore } from '~/store/cartStore';
import { useUIStore } from '~/store/uiStore';
import { ModalTypes } from '@/types/enums';
import { useLogoutAndClearCart } from '~/composables/useLogout';

const shouldWiggle = ref(false);

const triggerWiggle = () => {
  if (numProducts.value > 0) {
    shouldWiggle.value = true;
    setTimeout(() => {
      shouldWiggle.value = false;
    }, 500);
  }
};

const checkLogin = () => {
  if (!isLoggedIn.value) {
    uiStore.setModalType(ModalTypes.LOGIN);
  }
  else{
    uiStore.setMobileMenu(false);
  }
};

let wiggleInterval: number;

const userStore = useUserStore();
const uiStore = useUIStore();
const isLoggedIn = computed(() => userStore.userData?.is_logged_in || false);
const userEmail = computed(() => userStore.userData?.email_address || '');
const mainMenuItems = computed(() => uiStore.mainMenuItems || '');

const { logoutAndClearCart } = useLogoutAndClearCart();

const handleLogout = async () => {
  await logoutAndClearCart();  // call the logout action from userStore  
};

const cartStore = useCartStore(); // Initialize the cart store

watch(isLoggedIn, async (newVal) => {
    await cartStore.fetchCart();    
}, { immediate: true });


const numProducts = computed(() => cartStore.getQuantity);

//const mainMenuItems = uiStore.mainMenuItems;//ref<MenuItem[]>([]);

onMounted(async () => {
    wiggleInterval = setInterval(triggerWiggle, 30000);
});

onBeforeUnmount(() => {
  clearInterval(wiggleInterval); // Clear the interval when component is destroyed to prevent memory leaks
});

</script>

<style scoped lang="scss">
@import '~/assets/constants.scss';

$triangle-size: 0.5rem;
$sub-menu-color: rgba($fa-dark-gray, 1.0);

@keyframes wiggle {
  0% { transform: rotate(0deg); }
  25% { transform: rotate(-10deg); }
  50% { transform: rotate(0deg); }
  75% { transform: rotate(10deg); }
  100% { transform: rotate(0deg); }
}

.wiggle-effect {
  animation: wiggle 0.5s ease-in-out 1, background 0.2s ease-in-out 1;
  //display: inline-block;  /* Required for inline elements like <span> */
    background: $fa-orange;
}

nav {
  display: flex;
  align-items: center;
}

.menu-item-container {
  position: relative;
  display: flex;
  align-items: center;
  
  &:after {
    content: '';
    position: absolute;
    height: $triangle-size;  // same as the triangle
    left: 0;
    right: 0;
    top: 100%;
    z-index: -1;  // so it doesn't block anything visually or interactively
  }

  //.sub-menu {display: block;}
  
  &:hover .sub-menu {
    display: block;
  }

}

.sub-menu {
  display: none;
  position: absolute;
  z-index: 999;
  top: 100%;
  left: 0;
  list-style: none;
  color: $light-white;
  background: $sub-menu-color;
  border-radius: $fa-border-radius;
  box-shadow: 0 0.1rem 0.5rem rgba($black, 0.9);
  text-align: center;
  margin: 0;
  padding: 1rem;
  left: 1.1rem;
  min-width: 6rem;
  
  &::before {
    content: '';
    position: absolute;
    top: -$triangle-size + 0.1rem;
    left: 0.75rem;
    border-left: $triangle-size solid transparent;
    border-right: $triangle-size solid transparent;
    border-bottom: $triangle-size solid $sub-menu-color;
    display: block;
    z-index: 1;
  }
}

.nav-active {
  color: $fa-orange !important;
  &:hover {
    color: $light-white !important;
  }
}

.main-menu {
  justify-content: end;
  margin-right: $inner-wrap-margin;
  //background: rgba($white, 0.4);  
}

.main-nav-icon{
  padding: 0.65rem 1.2125em !important;
  .fa-cart-shopping {
        position: relative;
        
        .cart-badge {
            position: absolute;

            top: -0.65rem;  // Adjust these values to your liking to position the badge appropriately.
            right: -0.8rem;
            background-color: $fa-orange;  // Background color of the badge
            background: linear-gradient(to bottom, $fa-orange, $fa-dark-orange);
            color: $light-black;  // Text color of the badge
            border-radius: 50%;  // Makes the badge circular.
            //border-radius: $fa-border-radius;
            border: 0.1rem solid $light-white;
            padding: 0.1rem;
            //padding: 0.125rem 0.125rem;  // Adjust padding for the badge size
            font-size: 0.5rem;
            min-width: 0.8rem;  // Ensures the badge remains circular even with 1 digit.
            min-height: 0.8rem;
            display: flex;
            justify-content: center;  // Center horizontally.
            align-items: center;      // Center vertically.
            
            // Modifier for when cart-empty is active
            &.cart-empty {
              background: transparent;
              color: transparent;
              border-color: transparent;
            }
        }
    }
}

.nav-link {
  letter-spacing: 0.15rem;
  font-size: 1.25rem;
  margin-right: 2%;
  font-weight: bolder;
  line-height: 2.5rem;
  padding: 0 1.2125em;
  text-decoration: none;
  color: $light-white;
  border-radius: $fa-border-radius;
  transition: background 0.25s, transform 0.15s, box-shadow 0.25s;
  display: flex;
  justify-content: center;
  text-align: center;
  text-transform: uppercase;
  align-items: center;
  height: min-content;

  &:hover {
    background: linear-gradient(to bottom, $fa-orange, $fa-dark-orange);
    color: $white;
    box-shadow: 0px 0px 1rem rgba($white, 0.3);
  }
}

.logout-link {
  color: $fa-orange;
  font-style: italic;
  &:hover {
    color: $fa-dark-orange;
  }
}
</style>
